// Rostr — Welcome Experience (redesigned, compact B2B SaaS).
// Fase 6 UX Polish + "since last visit" semantics.
//
// Precedence guards from Fase 6.5B.2 preserved: never renders when the
// legal gate is unresolved or active. Session-only skip via sessionStorage
// so a browser refresh in the same tab does not re-open the panel;
// permanent opt-out lives in the server-side user preference
// `show_login_welcome_summary`.
//
// Mark-seen policy (Fase 6 UX Polish, Feb 2026):
//   * Screen 1 skip/X          → session-skip ONLY (no server call, no mark).
//   * Screen 2 back            → back to screen 1 (no mark).
//   * Screen 2 primary CTA     → mark seen + session-skip.
//   * Screen 2 close (X)       → mark seen + session-skip.
//   * Screen 2 item click      → mark seen + session-skip + navigate.
//   * Only ONE mark-seen call per Welcome flow.
//   * Network failure on mark-seen: swallow, do NOT block or retry.
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Bell, ClipboardList, Repeat, Clock, Calendar as CalIcon, Sparkles,
  Sunrise, Sun, Sunset, Moon, X, ArrowRight, CheckCircle2, FileCheck2,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";

const sessionSkipKey = (uid) => `rostr.welcome.session.${uid}`;

// key → { icon, i18n key, route }. Every key defined in the server response
// must map here or the item is silently dropped.
const OUTSTANDING_MAP = {
  unread_notifications: { icon: Bell,          tKey: "welcome_unread_notifications", route: "/notifications" },
  pending_leave:        { icon: ClipboardList, tKey: "welcome_pending_leave",        route: "/leave" },
  pending_swaps:        { icon: Repeat,        tKey: "welcome_pending_swaps",        route: "/swaps" },
  pending_time:         { icon: Clock,         tKey: "welcome_pending_time",         route: "/hours" },
  open_shifts:          { icon: CalIcon,       tKey: "welcome_open_shifts",          route: "/roster" },
  my_shifts_today:      { icon: CalIcon,       tKey: "welcome_my_shifts_today",      route: "/my-roster" },
  my_shifts_week:       { icon: CalIcon,       tKey: "welcome_my_shifts_week",       route: "/my-roster" },
  pending_own_leave:    { icon: ClipboardList, tKey: "welcome_pending_own_leave",    route: "/leave" },
  claimable_shifts:     { icon: Sparkles,      tKey: "welcome_claimable_shifts",     route: "/roster" },
  documents_pending_ack: { icon: FileCheck2,    tKey: "welcome_documents_pending_ack", route: "/documents" },
};
const NEW_MAP = {
  new_notifications:      { icon: Bell,          tKey: "welcome_new_notifications",      route: "/notifications" },
  new_leave_requests:     { icon: ClipboardList, tKey: "welcome_new_leave_requests",     route: "/leave" },
  new_swap_requests:      { icon: Repeat,        tKey: "welcome_new_swap_requests",      route: "/swaps" },
  new_time_submissions:   { icon: Clock,         tKey: "welcome_new_time_submissions",   route: "/hours" },
  new_open_shifts:        { icon: CalIcon,       tKey: "welcome_new_open_shifts",        route: "/roster" },
  new_own_leave_decided:  { icon: CheckCircle2,  tKey: "welcome_new_own_leave_decided",  route: "/leave" },
  new_swap_offers:        { icon: Repeat,        tKey: "welcome_new_swap_offers",        route: "/swaps" },
  new_my_shifts:          { icon: CalIcon,       tKey: "welcome_new_my_shifts",          route: "/my-roster" },
  new_documents_published: { icon: FileCheck2,   tKey: "welcome_new_documents_published", route: "/documents" },
};

function greetingKey() {
  const h = new Date().getHours();
  if (h < 6)  return { key: "welcome_good_evening", Icon: Moon };
  if (h < 12) return { key: "welcome_good_morning", Icon: Sunrise };
  if (h < 18) return { key: "welcome_good_afternoon", Icon: Sun };
  return { key: "welcome_good_evening", Icon: Sunset };
}

export default function WelcomeIntro() {
  const { user, legalRequired, legalStatusLoaded, refreshUser } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [summary, setSummary] = useState(null);
  const markedSeenRef = useRef(false);

  useEffect(() => {
    if (!user?._id) return;
    if (!legalStatusLoaded || legalRequired) return;
    if (user.show_login_welcome_summary === false) return;
    try { if (sessionStorage.getItem(sessionSkipKey(user._id)) === "1") return; } catch { /* ignore */ }
    setVisible(true);
    // Phase 9B.3b6 — when the fetch resolves and there's nothing
    // actionable/new for this returning user, silently dismiss instead of
    // repeating stale "outstanding" copy every visit. Fire mark-seen so the
    // next fetch has a fresh baseline.
    api.get("/welcome/summary")
      .then((r) => {
        const data = r.data || { first_use: true, new_items: [], outstanding_items: [] };
        setSummary(data);
        const anyNew = (data.new_items || []).some((it) => (it.count || 0) > 0 && NEW_MAP[it.key]);
        const anyOut = (data.outstanding_items || []).some((it) => (it.count || 0) > 0 && OUTSTANDING_MAP[it.key]);
        if (!data.first_use && !anyNew && !anyOut) {
          try { sessionStorage.setItem(sessionSkipKey(user._id), "1"); } catch { /* ignore */ }
          api.post("/welcome/summary/seen").catch(() => {});
          setVisible(false);
        }
      })
      .catch(() => setSummary({ first_use: true, new_items: [], outstanding_items: [] }));
  }, [user, legalRequired, legalStatusLoaded]);

  const markSeen = async () => {
    if (markedSeenRef.current) return;
    markedSeenRef.current = true;
    try {
      await api.post("/welcome/summary/seen");
      // Best-effort refresh so /auth/me reflects the new timestamp
      // for the next Welcome flow — do not block on failure.
      if (refreshUser) { try { await refreshUser(); } catch { /* ignore */ } }
    } catch { /* swallow — never block the user */ }
  };

  const sessionSkip = () => {
    try { if (user?._id) sessionStorage.setItem(sessionSkipKey(user._id), "1"); } catch { /* ignore */ }
    setVisible(false);
  };
  const closeWithMarkSeen = async () => {
    await markSeen();
    sessionSkip();
  };

  const firstUse = !!summary?.first_use;
  const newItems = useMemo(
    () => (summary?.new_items || []).filter((it) => it.count > 0 && NEW_MAP[it.key]),
    [summary],
  );
  const outstandingItems = useMemo(
    () => (summary?.outstanding_items || []).filter((it) => it.count > 0 && OUTSTANDING_MAP[it.key]),
    [summary],
  );
  const hasAny = newItems.length > 0 || outstandingItems.length > 0;
  const next = () => hasAny ? setStep(1) : sessionSkip();
  if (!visible || !user) return null;

  const firstName = (user.first_name || (user.name || "").split(" ")[0] || "").trim();
  const g = greetingKey();
  const introKey = firstUse ? "welcome_first_use_intro" : "welcome_since_last_visit_intro";
  const missedTitleKey = firstUse ? "welcome_first_use_title" : "welcome_missed_title";
  const missedSubtitleKey = firstUse ? "welcome_first_use_subtitle" : "welcome_missed_subtitle";

  const renderItem = (it, map, isNew) => {
    const meta = map[it.key];
    if (!meta) return null;
    const Icon = meta.icon;
    return (
      <button
        key={`${isNew ? "new" : "out"}-${it.key}`}
        onClick={async () => { await markSeen(); sessionSkip(); navigate(meta.route); }}
        data-testid={`welcome-item-${it.key}`}
        className="flex items-center gap-3 px-3 py-2.5 rounded-xl border hairline text-left hover:bg-[hsl(var(--surface-2))]"
      >
        <Icon size={16} className="text-[hsl(var(--text-muted))]" />
        <span className="text-[13px] text-[hsl(var(--text))] flex-1">
          {t(meta.tKey).replace("{n}", String(it.count))}
        </span>
        <span className="text-[13px] font-semibold text-[hsl(var(--text-strong))] tabular-nums">
          {it.count}
        </span>
      </button>
    );
  };

  // Bounded total: max 6 items on screen 2 (new first, then outstanding).
  const shownNew = newItems.slice(0, 6);
  const remainingSlots = Math.max(0, 6 - shownNew.length);
  const shownOutstanding = outstandingItems.slice(0, remainingSlots);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[9999] grid place-items-center px-4 py-8 bg-[hsl(var(--bg)/0.85)] backdrop-blur-sm"
        data-testid="welcome-intro"
        data-first-use={firstUse ? "1" : "0"}
      >
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="card p-6 md:p-8 w-full max-w-[560px] relative"
        >
          {step === 0 ? (
            <button
              onClick={sessionSkip}
              data-testid="welcome-close"
              aria-label={t("welcome_close")}
              className="absolute top-4 right-4 w-8 h-8 grid place-items-center rounded-full text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-strong))]"
            >
              <X size={16} strokeWidth={2.2} />
            </button>
          ) : (
            <button
              onClick={closeWithMarkSeen}
              data-testid="welcome-close-marks-seen"
              aria-label={t("welcome_close")}
              className="absolute top-4 right-4 w-8 h-8 grid place-items-center rounded-full text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-strong))]"
            >
              <X size={16} strokeWidth={2.2} />
            </button>
          )}
          <div className="text-[12px] font-semibold text-[hsl(var(--text-muted))] flex items-center gap-2 mb-2">
            <g.Icon size={14} strokeWidth={2.2} />
            <span>{t(g.key)}</span>
          </div>
          {step === 0 ? (
            <>
              <h1 className="font-display text-[22px] md:text-[26px] tracking-tight text-[hsl(var(--text-strong))] mb-2" data-testid="welcome-hello">
                {firstName ? t("welcome_back_name").replace("{firstName}", firstName) : t("welcome_back_generic")}
              </h1>
              <p className="text-[14px] text-[hsl(var(--text-muted))] mb-6" data-testid="welcome-intro-copy">
                {t(introKey)}
              </p>
              <div className="flex flex-wrap gap-2 justify-end">
                <button onClick={sessionSkip} className="btn btn-ghost" data-testid="welcome-skip">
                  {t("welcome_skip_now")}
                </button>
                <button onClick={next} className="btn btn-primary flex items-center gap-1.5" data-testid="welcome-next">
                  {t("welcome_view_summary")}
                  <ArrowRight size={14} />
                </button>
              </div>
              {hasAny && (
                <div className="text-[11px] text-[hsl(var(--text-muted))] mt-4 tabular-nums" data-testid="welcome-progress">
                  {t("welcome_step_of").replace("{a}", "1").replace("{b}", "2")}
                </div>
              )}
            </>
          ) : (
            <>
              <h2 className="font-display text-[20px] md:text-[24px] tracking-tight text-[hsl(var(--text-strong))] mb-1" data-testid="welcome-summary-title">
                {hasAny ? t(missedTitleKey) : t("welcome_all_caught_up")}
              </h2>
              <p className="text-[13px] text-[hsl(var(--text-muted))] mb-5" data-testid="welcome-summary-subtitle">
                {hasAny ? t(missedSubtitleKey) : t("welcome_no_updates")}
              </p>
              {hasAny ? (
                <div className="flex flex-col gap-4 mb-5" data-testid="welcome-summary-list">
                  {shownNew.length > 0 && (
                    <div data-testid="welcome-group-new">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))] mb-2">
                        {t("welcome_group_new")}
                      </div>
                      <div className="flex flex-col gap-2">
                        {shownNew.map((it) => renderItem(it, NEW_MAP, true))}
                      </div>
                    </div>
                  )}
                  {shownOutstanding.length > 0 && (
                    <div data-testid="welcome-group-outstanding">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))] mb-2">
                        {t("welcome_group_outstanding")}
                      </div>
                      <div className="flex flex-col gap-2">
                        {shownOutstanding.map((it) => renderItem(it, OUTSTANDING_MAP, false))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
              <div className="flex justify-between items-center flex-wrap gap-2">
                <button onClick={() => setStep(0)} className="btn btn-ghost" data-testid="welcome-back">
                  {t("welcome_back_button")}
                </button>
                <button onClick={closeWithMarkSeen} className="btn btn-primary" data-testid="welcome-go">
                  {hasAny ? t("welcome_skip_now") : t("welcome_go_to_rostr")}
                </button>
              </div>
              <div className="text-[11px] text-[hsl(var(--text-muted))] mt-4 tabular-nums" data-testid="welcome-progress">
                {t("welcome_step_of").replace("{a}", "2").replace("{b}", "2")}
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
